import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { extractShortCode } from "../lib/extractShortCode";

type Mode = "starting" | "scanning" | "denied" | "error";

interface Props {
  onClose: () => void;
  onShortCode: (shortCode: string) => void;
}

export function JoinQrScanner({ onClose, onShortCode }: Props) {
  const containerId = "join-qr-scanner-region";
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [mode, setMode] = useState<Mode>("starting");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const handledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const scanner = new Html5Qrcode(containerId, {
      verbose: false,
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
    });
    scannerRef.current = scanner;

    scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => {
          if (handledRef.current) return;
          const shortCode = extractShortCode(decoded);
          if (!shortCode) {
            setHint("That QR isn't an Orbit Join code. Try another.");
            return;
          }
          handledRef.current = true;
          scanner
            .stop()
            .catch(() => undefined)
            .finally(() => onShortCode(shortCode));
        },
        () => {
          // per-frame decode failures — ignore quietly
        },
      )
      .then(() => {
        if (cancelled) {
          scanner.stop().catch(() => undefined);
          return;
        }
        setMode("scanning");
      })
      .catch((err: unknown) => {
        const name = (err as { name?: string })?.name ?? "";
        if (
          name === "NotAllowedError" ||
          name === "PermissionDeniedError"
        ) {
          setMode("denied");
        } else {
          setMode("error");
          setErrMsg(
            (err as { message?: string })?.message ??
              "Couldn't start the camera.",
          );
        }
      });

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        s.stop()
          .catch(() => undefined)
          .finally(() => {
            try {
              s.clear();
            } catch {
              // best-effort
            }
          });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Scan a merchant Join QR"
      className="fixed inset-0 z-50 flex flex-col bg-navy/95"
    >
      <div className="flex items-center justify-between px-5 pt-5 text-white">
        <h2 className="text-base font-semibold">Scan Join QR</h2>
        <button
          onClick={onClose}
          className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium"
          aria-label="Close scanner"
        >
          Close
        </button>
      </div>

      <div className="relative mx-auto mt-6 w-full max-w-sm flex-1 px-5">
        <div className="aspect-square w-full overflow-hidden rounded-2xl bg-black/40 ring-1 ring-white/10">
          <div id={containerId} className="h-full w-full" />
        </div>
        {mode === "starting" && (
          <p className="mt-4 text-center text-sm text-white/80">
            Starting camera…
          </p>
        )}
        {mode === "scanning" && (
          <p className="mt-4 text-center text-sm text-white/80">
            Point at the merchant&apos;s Join QR.
          </p>
        )}
        {hint && mode === "scanning" && (
          <p className="mt-2 text-center text-xs text-amber-200">{hint}</p>
        )}
        {mode === "denied" && (
          <div className="mt-4 rounded-xl bg-white/10 p-4 text-sm text-white">
            <p className="font-medium">Camera permission was blocked.</p>
            <p className="mt-1 text-white/80">
              Allow camera access in your browser settings, then reopen the
              scanner. Or paste the merchant&apos;s join code on the next
              screen.
            </p>
          </div>
        )}
        {mode === "error" && (
          <div className="mt-4 rounded-xl bg-white/10 p-4 text-sm text-white">
            <p className="font-medium">Couldn&apos;t start the camera.</p>
            <p className="mt-1 text-white/80">
              {errMsg ?? "Try again or use a different device."}
            </p>
          </div>
        )}
      </div>

      <p className="px-5 pb-6 pt-4 text-center text-[11px] text-white/60">
        Orbit by M42
      </p>
    </div>
  );
}
